
import React, { useState } from 'react';
import { Award, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import ProjectItem from './ProjectItem';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface Project {
  id?: string;
  title: string;
  description?: string | null;
  project_url?: string | null;
  skills?: string[];
}

interface ProjectsSectionProps {
  userId: string;
  projects: Project[];
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
}

const ProjectsSection: React.FC<ProjectsSectionProps> = ({ userId, projects, setProjects }) => {
  const { toast } = useToast();
  const [newProject, setNewProject] = useState<Project>({
    title: '',
    description: '',
    project_url: '',
    skills: []
  });
  const [newSkill, setNewSkill] = useState('');
  const [formErrors, setFormErrors] = useState<{title?: string}>({});
  const [dialogOpen, setDialogOpen] = useState(false);

  const validateForm = () => {
    const errors: {title?: string} = {};
    
    if (!newProject.title.trim()) {
      errors.title = "Project title is required";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddSkill = () => {
    if (!newSkill.trim()) return;
    
    // Split by commas if user inputs multiple skills
    const skillsToAdd = newSkill
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);
    
    setNewProject(prev => ({
      ...prev,
      skills: [...(prev.skills || []), ...skillsToAdd]
    }));
    
    setNewSkill('');
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setNewProject(prev => ({
      ...prev,
      skills: (prev.skills || []).filter(skill => skill !== skillToRemove)
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const handleAddProject = async () => {
    if (!validateForm()) return;
    
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('job_seeker_projects')
        .insert({
          job_seeker_id: userId,
          title: newProject.title,
          description: newProject.description,
          project_url: newProject.project_url
        })
        .select('id')
        .single();
      
      if (projectError) throw projectError;

      if (newProject.skills && newProject.skills.length > 0) {
        const skillInserts = newProject.skills.map(skill => ({
          project_id: projectData.id,
          skill_name: skill
        }));

        const { error: skillsError } = await supabase
          .from('project_skills')
          .insert(skillInserts);
        
        if (skillsError) throw skillsError;
      }

      setProjects(prev => [...prev, { ...newProject, id: projectData.id }]);
      
      setNewProject({
        title: '',
        description: '',
        project_url: '',
        skills: []
      });
      
      setDialogOpen(false);

      toast({
        title: "Project Added",
        description: "Your project has been successfully added to your profile.",
      });
    } catch (error) {
      console.error('Error adding project:', error);
      toast({
        title: "Error",
        description: "Failed to add project. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateProject = (id: string, updatedProject: any) => {
    setProjects(prev => 
      prev.map(project => 
        project.id === id 
          ? { ...project, ...updatedProject } 
          : project
      )
    );
  };

  const renderProjectDialog = () => (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full flex items-center justify-center gap-2 text-[#0EA5E9] border-[#0EA5E9]/30"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="h-4 w-4" /> Add Project
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Input 
              placeholder="Project Title *" 
              value={newProject.title}
              onChange={(e) => {
                setNewProject(prev => ({ ...prev, title: e.target.value }));
                if (e.target.value.trim()) {
                  setFormErrors(prev => ({ ...prev, title: undefined }));
                }
              }}
              className={formErrors.title ? "border-red-500" : ""}
            />
            {formErrors.title && (
              <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
            )}
          </div>
          
          <Textarea 
            placeholder="Project Description (Optional)" 
            value={newProject.description}
            onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
          />
          
          <Input 
            placeholder="Project URL (Optional)" 
            value={newProject.project_url}
            onChange={(e) => setNewProject(prev => ({ ...prev, project_url: e.target.value }))}
          />
          
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input 
                placeholder="Add Skills (comma-separated or press Enter)" 
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button 
                type="button"
                onClick={handleAddSkill} 
                className="bg-[#0EA5E9] text-white flex-shrink-0"
              >
                Add
              </Button>
            </div>
            
            {newProject.skills && newProject.skills.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {newProject.skills.map((skill, index) => (
                  <Badge key={index} className="bg-[#0EA5E9]/90 flex items-center gap-1">
                    {skill}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => handleRemoveSkill(skill)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <Button 
            onClick={handleAddProject} 
            className="w-full bg-[#0EA5E9] text-white"
          >
            Save Project
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="flex items-start gap-4">
      <div className="bg-[#0EA5E9]/10 p-2 rounded-full">
        <Award className="h-5 w-5 text-[#0EA5E9]" />
      </div>
      <div className="w-full">
        <h3 className="text-sm font-medium text-gray-800">Projects</h3>
        {projects.length > 0 ? (
          <>
            {projects.map(project => (
              <ProjectItem 
                key={project.id} 
                {...project} 
                onUpdate={handleUpdateProject}
              />
            ))}
            {renderProjectDialog()}
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">No projects added yet</p>
            {renderProjectDialog()}
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectsSection;
